/**
 * APX WorkCenter — PreviewContext
 * Allows super_admin to temporarily preview the UI as another role.
 * The actual authenticated user never changes — only the rendered view.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { UserRole } from "./AuthContext";

export type PreviewableRole = "controller" | "asst_controller" | "bookkeeping_manager" | "bookkeeper_supervisor" | "bookkeeper" | "tax_division";

interface PreviewContextValue {
  previewRole: PreviewableRole | null;
  setPreviewRole: (role: PreviewableRole | null) => void;
  isPreviewMode: boolean;
  exitPreview: () => void;
}

const PreviewContext = createContext<PreviewContextValue | null>(null);

export function PreviewProvider({ children }: { children: React.ReactNode }) {
  const [previewRole, setPreviewRoleState] = useState<PreviewableRole | null>(null);

  const setPreviewRole = useCallback((role: PreviewableRole | null) => {
    setPreviewRoleState(role);
  }, []);

  const exitPreview = useCallback(() => {
    setPreviewRoleState(null);
  }, []);

  const value = useMemo<PreviewContextValue>(() => ({
    previewRole,
    setPreviewRole,
    isPreviewMode: previewRole !== null,
    exitPreview,
  }), [previewRole, setPreviewRole, exitPreview]);

  return (
    <PreviewContext.Provider value={value}>
      {children}
    </PreviewContext.Provider>
  );
}

export function usePreview() {
  const ctx = useContext(PreviewContext);
  if (!ctx) throw new Error("usePreview must be used within PreviewProvider");
  return ctx;
}

/**
 * Returns the effective role for rendering purposes.
 * If in preview mode, returns the preview role; otherwise returns the actual role.
 */
export function useEffectiveRole(actualRole: UserRole): UserRole {
  const { previewRole } = usePreview();
  // normalizeRole maps "admin" → "super_admin" so nav items with roles:["super_admin"]
  // are always visible to admin users. This is the single place where that mapping lives.
  const base = previewRole ?? actualRole;
  return base === "admin" ? ("super_admin" as UserRole) : base;
}
